import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {map, tap} from 'rxjs/operators';
import {AgroReportActions} from './agro-report.actions';
import {AgroReportService} from '../../services/agro-report.service';
import {AgroReportCumulation, AgroReportSubvention} from '../../models/api/agro/agro-report.model';
import {DateTime} from 'luxon';
import {DateRange} from '../../models/api/date-range.model';

interface AgroReportStateModel {
    subventionYear: number;
    subventionStartYear: number;
    subventionReport: AgroReportSubvention;
    cumulationPeriods: DateRange[];
    cumulationReport: AgroReportCumulation;
}

@State<AgroReportStateModel>({
    name: AgroReportState.STATE_NAME,
    defaults: {
        subventionYear: DateTime.local().year,
        subventionStartYear: 2022,
        subventionReport: null,
        cumulationPeriods: [],
        cumulationReport: null,
    },
})
@Injectable()
export class AgroReportState {
    static readonly STATE_NAME = 'agroReport';
    readonly dateFormat = 'yyyy-MM-dd';

    constructor(private agroReportService: AgroReportService) {}

    @Selector()
    static cumulationPeriods(state: AgroReportStateModel) {
        return state.cumulationPeriods;
    }

    @Selector()
    static cumulationReport(state: AgroReportStateModel) {
        return state.cumulationReport;
    }

    @Selector()
    static subventionReport(state: AgroReportStateModel) {
        return state.subventionReport;
    }

    @Selector()
    static subventionYear(state: AgroReportStateModel) {
        return state.subventionYear;
    }

    @Selector()
    static subventionStartYear(state: AgroReportStateModel) {
        return state.subventionStartYear;
    }

    @Action(AgroReportActions.GetCumulationReport)
    getCumulationReport(ctx: StateContext<AgroReportStateModel>, {date}: AgroReportActions.GetCumulationReport) {
        return this.agroReportService.getCumulationReport(date ?? DateTime.local().toFormat(this.dateFormat)).pipe(
            map((cumulationReport) =>
                ctx.patchState({
                    cumulationReport: {
                        dateRange: this.formatDateRange(cumulationReport.dateRange),
                        limitsPerVoivodeship: cumulationReport.limitsPerVoivodeship,
                    },
                })
            )
        );
    }

    @Action(AgroReportActions.GetCumulationPeriods)
    getCumulationPeriods(ctx: StateContext<AgroReportStateModel>) {
        return this.agroReportService.getCumulationPeriods().pipe(
            map((cumulationPeriods) =>
                ctx.patchState({
                    cumulationPeriods: cumulationPeriods.map(this.formatDateRange),
                })
            )
        );
    }

    @Action(AgroReportActions.GetSubventionReport)
    getSubventionReport(ctx: StateContext<AgroReportStateModel>, {year}: AgroReportActions.GetSubventionReport) {
        return this.agroReportService
            .getSubventionReport(year ?? ctx.getState().subventionYear)
            .pipe(tap((subventionReport) => ctx.patchState({subventionReport})));
    }

    protected formatDateRange = (value: DateRange) => ({
        start: DateTime.fromISO(value.start).toFormat(this.dateFormat),
        stop: DateTime.fromISO(value.stop).toFormat(this.dateFormat),
    });
}
